import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';
import {
  CircleMarker,
  MapContainer,
  Marker,
  TileLayer,
  useMap,
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

const CASTLE_CENTER = [41.80555, -6.749];
const DEFAULT_ZOOM = 18;
const FINALE_ZOOM = 17;
const MAX_ZOOM = 21;
const MAX_NATIVE_ZOOM = 19;
const MIN_ZOOM = 14;
const BOUNDS_PADDING = [48, 48];

function createSealIcon() {
  return L.divIcon({
    className: 'fh-map-seal-marker',
    html: `<span class="fh-map-seal" aria-hidden="true">
      <svg viewBox="0 0 40 48" width="44" height="52">
        <path fill="#6b3a1f" stroke="#F4B366" stroke-width="1.5"
          d="M20 2c-7 0-13 5.5-13 12.5 0 9.5 13 31 13 31s13-21.5 13-31C33 7.5 27 2 20 2z"/>
        <circle cx="20" cy="15" r="6" fill="#1A132E" stroke="#F4B366" stroke-width="1.2"/>
        <path d="M17 15h6M20 12v6" stroke="#F4B366" stroke-width="1.4" stroke-linecap="round"/>
      </svg>
    </span>`,
    iconSize: [44, 52],
    iconAnchor: [22, 52],
    popupAnchor: [0, -46],
  });
}

function createFinaleIcon() {
  return L.divIcon({
    className: 'fh-map-seal-marker',
    html: `<span class="fh-map-seal fh-map-seal--finale" aria-hidden="true">
      <svg viewBox="0 0 40 48" width="52" height="62">
        <path fill="#1A132E" stroke="#F4B366" stroke-width="1.8"
          d="M20 2c-7 0-13 5.5-13 12.5 0 9.5 13 31 13 31s13-21.5 13-31C33 7.5 27 2 20 2z"/>
        <circle cx="20" cy="15" r="7" fill="#F4B366" stroke="#1A132E" stroke-width="1.2"/>
        <path d="M17 15h6M20 12v6" stroke="#1A132E" stroke-width="1.6" stroke-linecap="round"/>
      </svg>
    </span>`,
    iconSize: [52, 62],
    iconAnchor: [26, 62],
    popupAnchor: [0, -54],
  });
}

function FitToPoints({ points, fallbackCenter, fallbackZoom }) {
  const map = useMap();

  useEffect(() => {
    const valid = (points || []).filter(
      (p) => Array.isArray(p) && p.length >= 2 && Number.isFinite(p[0]) && Number.isFinite(p[1]),
    );

    if (valid.length === 0) {
      map.setView(fallbackCenter, fallbackZoom, { animate: false });
    } else if (valid.length === 1) {
      map.setView(valid[0], fallbackZoom, { animate: false });
    } else {
      const bounds = L.latLngBounds(valid);
      map.fitBounds(bounds, {
        padding: BOUNDS_PADDING,
        maxZoom: DEFAULT_ZOOM,
        animate: false,
      });
    }

    const t = window.setTimeout(() => map.invalidateSize(), 120);
    return () => window.clearTimeout(t);
  }, [map, points, fallbackCenter, fallbackZoom]);

  return null;
}

function FollowUser({ position, enabled, onFollowed }) {
  const map = useMap();
  useEffect(() => {
    if (!enabled || !position) return;
    map.flyTo(position, Math.max(map.getZoom(), 19), { duration: 0.8 });
    const t = window.setTimeout(onFollowed, 900);
    return () => window.clearTimeout(t);
  }, [enabled, position, map, onFollowed]);
  return null;
}

export default function CastleMap({
  enigmas = [],
  finale = null,
  showLocate = true,
}) {
  const navigate = useNavigate();
  const sealIcon = useMemo(() => createSealIcon(), []);
  const finaleIcon = useMemo(() => createFinaleIcon(), []);
  const [geoStatus, setGeoStatus] = useState('idle');
  const [userPos, setUserPos] = useState(null);
  const [followOnce, setFollowOnce] = useState(false);
  const clearFollow = useCallback(() => setFollowOnce(false), []);

  const markerPoints = useMemo(() => {
    const points = enigmas.map((enigma) => enigma.position).filter(Boolean);
    if (finale?.position) points.push(finale.position);
    return points;
  }, [enigmas, finale]);

  const mapCenter = finale?.position || CASTLE_CENTER;
  const mapZoom = finale ? FINALE_ZOOM : DEFAULT_ZOOM;

  useEffect(() => {
    if (geoStatus !== 'granted' || !navigator.geolocation) return;

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        setUserPos([pos.coords.latitude, pos.coords.longitude]);
      },
      () => {
        setGeoStatus('denied');
        setUserPos(null);
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 },
    );

    return () => navigator.geolocation.clearWatch(watchId);
  }, [geoStatus]);

  function requestLocation() {
    if (!navigator.geolocation) {
      setGeoStatus('unavailable');
      return;
    }
    setGeoStatus('pending');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserPos([pos.coords.latitude, pos.coords.longitude]);
        setGeoStatus('granted');
        setFollowOnce(true);
      },
      () => {
        setGeoStatus('denied');
        setUserPos(null);
      },
      { enableHighAccuracy: true, timeout: 12000 },
    );
  }

  return (
    <div className="fh-castle-map">
      <MapContainer
        center={mapCenter}
        zoom={mapZoom}
        minZoom={MIN_ZOOM}
        maxZoom={MAX_ZOOM}
        scrollWheelZoom
        zoomControl={false}
        className="fh-castle-map__leaflet"
        attributionControl={false}
      >
        <TileLayer
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          maxZoom={MAX_ZOOM}
          maxNativeZoom={MAX_NATIVE_ZOOM}
        />
        <FitToPoints
          points={markerPoints}
          fallbackCenter={mapCenter}
          fallbackZoom={mapZoom}
        />
        <FollowUser
          position={userPos}
          enabled={followOnce}
          onFollowed={clearFollow}
        />
        {enigmas.map((enigma) => (
          <Marker
            key={enigma.id}
            position={enigma.position}
            icon={sealIcon}
            eventHandlers={{
              click: () => navigate(`/festa-historia/enigma/${enigma.id}`),
            }}
          />
        ))}
        {finale && (
          <Marker
            position={finale.position}
            icon={finaleIcon}
            eventHandlers={{
              click: () => navigate('/festa-historia/concluido'),
            }}
          />
        )}
        {userPos && (
          <>
            <CircleMarker
              center={userPos}
              radius={18}
              pathOptions={{
                color: '#F4B366',
                fillColor: '#4a90d9',
                fillOpacity: 0.2,
                weight: 1,
                opacity: 0.5,
              }}
            />
            <CircleMarker
              center={userPos}
              radius={8}
              pathOptions={{
                color: '#ffffff',
                fillColor: '#4a90d9',
                fillOpacity: 1,
                weight: 2,
              }}
            />
          </>
        )}
      </MapContainer>

      {showLocate && (
        <div className="fh-castle-map__toolbar">
          {geoStatus !== 'granted' && (
            <button
              type="button"
              className="fh-locate-btn"
              onClick={requestLocation}
              disabled={geoStatus === 'pending'}
            >
              {geoStatus === 'pending' ? 'A obter…' : 'A minha localização'}
            </button>
          )}
          {geoStatus === 'granted' && userPos && (
            <button
              type="button"
              className="fh-locate-btn fh-locate-btn--active"
              onClick={() => setFollowOnce(true)}
            >
              Centrar em mim
            </button>
          )}
          {geoStatus === 'denied' && (
            <span className="fh-locate-hint">Localização recusada</span>
          )}
          {geoStatus === 'unavailable' && (
            <span className="fh-locate-hint">GPS indisponível</span>
          )}
        </div>
      )}
    </div>
  );
}
